import type { Session, Task } from '@timato/shared';
import type { Goal } from './goal';
import { isBreak } from './engine';

/** How many parked timers are offered: one per number key, 1 to 9 and then 0. */
export const SWITCH_MAX = 10;

/** The key printed on, and picking, the `i`th target: 1-9, then 0 for the tenth, as the keys run along the row. */
export const switchKey = (i: number) => String((i + 1) % 10);

/** A run parked today (TW-32) that the clock can resume: the parked session holds its id and its time so far. */
export type SwitchTarget = { key: string; session: Session };

/** One key per goal, matching `sameGoal`: the task when there is one, otherwise the project and the words. */
const goalKey = (g: Goal) => (g.taskId ? `task:${g.taskId}` : `free:${g.projectId ?? ''}:${g.goalText.trim()}`);

/**
 * The parked timers on the Timer (TW-30): today's parked runs (TW-32), most recently parked first, one per goal. Every
 * switch parks the run it leaves, so these are the tasks in hand, and a pick always resumes. A finished goal is not
 * here, since Finish says it is done for now: starting it again is a new run, from the goal field. The goal on the
 * clock is left out, and so is a task since ticked off, since resuming it would mean reopening it.
 */
export function switchTargets(today: Session[], current: Goal, tasks: Map<string, Task>, max = SWITCH_MAX): SwitchTarget[] {
  const skip = goalKey(current);
  const byGoal = new Map<string, SwitchTarget>();
  for (const s of today) {
    if (s.status !== 'parked' || isBreak(s.mode)) continue;
    const key = goalKey(s);
    if (key === skip || (s.taskId && tasks.get(s.taskId)?.finishedAt)) continue;
    const seen = byGoal.get(key);
    if (!seen || s.endedAt > seen.session.endedAt) byGoal.set(key, { key, session: s });
  }
  return [...byGoal.values()].sort((a, b) => b.session.endedAt.localeCompare(a.session.endedAt)).slice(0, max);
}
